import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { useLang, useT } from '../i18n/LangContext';
import { useContent } from '../data/useContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm({ variant = 'card', source = 'website', idPrefix = 'quote' }) {
  const t = useT();
  const { lang } = useLang();
  const { SERVICES } = useContent();
  const TIMES = [t['qf.t1'], t['qf.t2'], t['qf.t3'], t['qf.t4']];
  const [params] = useSearchParams();
  const preService = params.get('service') || '';
  const [form, setForm] = useState({ service: preService, date: '', time: '', name: '', email: '', phone: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, source, lang });
      localStorage.setItem('rev-quote-submitted', '1');
      setDone(true);
      toast.success(t['qf.toastOk']);
    } catch {
      toast.error(t['qf.toastErr']);
    }
    setSending(false);
  };

  if (done) {
    return (
      <div data-testid={`${idPrefix}-success`} className={`${variant === 'card' ? 'rounded-2xl bg-white p-8 shadow-2xl' : ''} text-center`}>
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gold/15 text-gold-dim">
          <ArrowRight className="h-6 w-6" />
        </div>
        <h3 className="font-serif text-2xl font-semibold text-ink">{t['qf.successTitle']}</h3>
        <p className="mt-2 text-sm text-neutral-500">{t['qf.successSub']}</p>
      </div>
    );
  }

  const fields = (
    <form onSubmit={submit} className="space-y-3">
      <select data-testid={`${idPrefix}-service-select`} required value={form.service} onChange={set('service')} className="input-light" aria-label={t['qf.service']}>
        <option value="" disabled>{t['qf.service']}</option>
        {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-3">
        <input data-testid={`${idPrefix}-date-input`} type="date" value={form.date} onChange={set('date')} className="input-light" aria-label="Preferred date" />
        <select data-testid={`${idPrefix}-time-select`} value={form.time} onChange={set('time')} className="input-light" aria-label={t['qf.time']}>
          <option value="">{t['qf.time']}</option>
          {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
        </select>
      </div>
      <input data-testid={`${idPrefix}-name-input`} required placeholder={t['qf.name']} value={form.name} onChange={set('name')} className="input-light" aria-label={t['qf.name']} />
      <div className="grid grid-cols-2 gap-3">
        <input data-testid={`${idPrefix}-email-input`} required type="email" placeholder={t['qf.email']} value={form.email} onChange={set('email')} className="input-light" aria-label={t['qf.email']} />
        <input data-testid={`${idPrefix}-phone-input`} required type="tel" placeholder={t['qf.phone']} value={form.phone} onChange={set('phone')} className="input-light" aria-label={t['qf.phone']} />
      </div>
      <button data-testid={`${idPrefix}-submit-button`} type="submit" disabled={sending}
        className="gold-btn w-full disabled:opacity-60">
        {sending ? t['qf.sending'] : <>{t['qf.submit']} <ArrowRight className="h-4 w-4" /></>}
      </button>
      <p className="text-center text-[11px] leading-relaxed text-neutral-400">{t['qf.fine']}</p>
    </form>
  );

  if (variant === 'card') {
    return (
      <div data-testid={`${idPrefix}-card`} className="rounded-2xl bg-white p-7 text-ink shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] md:p-8">
        <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dim">
          <span className="inline-block h-2 w-2 bg-gold" /> {t['qf.eyebrow']}
        </span>
        <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight">{t['qf.title']}</h2>
        <p className="mb-5 mt-1.5 text-sm text-neutral-500">{t['qf.sub']}</p>
        {fields}
      </div>
    );
  }
  return <div data-testid={`${idPrefix}-form`}>{fields}</div>;
}
